import ClearIcon from "@mui/icons-material/Clear";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";
import { formatLocalDateTimeForApi } from "../../../../middleware/middleware";

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const validationSchema = Yup.object({
  requestId: Yup.number().min(1, "درخواست معتبر نیست").required(),
  scheduledVisitTime: Yup.date()
    .required("زمان هماهنگ‌شده بازدید الزامی است.")
    .test(
      "not-before-today",
      "زمان بازدید نمی‌تواند قبل از امروز باشد.",
      (value) => {
        if (!value) return false;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return false;
        return d.getTime() >= startOfToday().getTime();
      }
    ),
  description: Yup.string().required("لطفاً توضیحات هماهنگی را وارد کنید."),
});

const SetVisitDate = ({ open, onClose, id }) => {
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState({
    requestId: 0,
    scheduledVisitTime: "",
    description: "",
  });
  const minVisitDate = useMemo(() => startOfToday(), [open]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && open) {
        try {
          setLoading(true);
          const res = await DefaultApiService.getOneApi(
            "SLN/Contractor/SiteVisit",
            id
          );
          const payload = res?.data || res;
          const visit = payload?.siteVisit || payload?.SiteVisit;
          if (visit) {
            setInitialData({
              requestId: visit.requestId || visit.RequestId || 0,
              scheduledVisitTime:
                visit.scheduledVisitTime &&
                !String(visit.scheduledVisitTime).startsWith("0001-01-01")
                  ? visit.scheduledVisitTime
                  : "",
              description: visit.description || "",
            });
          }
        } catch (err) {
          toast.error(err?.response?.data?.message || "خطا در دریافت اطلاعات");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, open]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setLoading(true);
      const res = await DefaultApiService.postApi(
        "SLN/Contractor/SiteVisit/SetVisitDate",
        {
          requestId: values.requestId,
          scheduledVisitTime: formatLocalDateTimeForApi(
            values.scheduledVisitTime
          ),
          description: values.description,
        }
      );
      if (res.isSuccess) {
        toast.success(res.message || "زمان بازدید با موفقیت ثبت شد.");
        resetForm();
        onClose(true);
      } else {
        toast.error(res.message || "خطا در ثبت زمان بازدید");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "خطا در ثبت تاریخ");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={ModalTransition}
      keepMounted
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
    >
      <Formik
        initialValues={initialData}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          } = formikProps;

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <IconButton onClick={() => onClose()}>
                      <ClearIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h5" fontWeight={600}>
                      هماهنگی زمان بازدید
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      زمان توافق‌شده با مشتری را ثبت کنید. پس از آن، از فرم
                      ویرایش، اطلاعات انجام بازدید را وارد می‌کنید.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <JalaliDateTimePicker
                      label="زمان هماهنگ‌شده بازدید"
                      value={values.scheduledVisitTime}
                      minDate={minVisitDate}
                      onChange={(val) => setFieldValue("scheduledVisitTime", val)}
                      error={
                        touched.scheduledVisitTime &&
                        Boolean(errors.scheduledVisitTime)
                      }
                      helperText={
                        touched.scheduledVisitTime && errors.scheduledVisitTime
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="توضیحات هماهنگی"
                      name="description"
                      value={values.description}
                      onChange={wrapFormikHandleChange(handleChange)}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      fullWidth
                      placeholder="مثلاً تماس با مشتری و توافق روی ساعت بازدید"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => onClose()} variant="outlined">
                  انصراف
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting || loading}
                >
                  {loading ? <CircularProgress size={24} /> : "ثبت زمان هماهنگ‌شده"}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default SetVisitDate;
